import { Link } from 'react-router-dom';
import { MessageCircle, ArrowUpRight } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import { Reveal } from './Motion';
import { CONTACT, FAQS } from '../lib/data';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

export default function FAQ() {
  return (
    <section data-testid="home-faq" className="bg-background">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Heading column */}
          <div className="lg:col-span-4">
            <Reveal>
              <p className="overline">06 — FAQs</p>
              <h2 className="font-display mt-6 text-4xl md:text-5xl font-medium tracking-tight leading-[1.1]">
                Frequently Asked Questions
              </h2>
              <p className="mt-6 text-muted-foreground leading-[1.6] max-w-md">
                Find answers to the most common questions about our printing, packaging, and branding solutions.
              </p>
            </Reveal>
          </div>

          {/* Accordion column */}
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <Accordion type="single" collapsible defaultValue="faq-0" className="space-y-4">
                {FAQS.map((f, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    data-testid={`faq-item-${i}`}
                    className="border border-border rounded-xl bg-card px-6 md:px-8 data-[state=open]:shadow-[0_10px_30px_rgb(0,0,0,0.05)] transition-shadow duration-300"
                  >
                    <AccordionTrigger
                      data-testid={`faq-trigger-${i}`}
                      className="py-6 text-base md:text-lg font-display font-medium text-foreground hover:no-underline [&[data-state=open]>svg]:text-accent gap-6"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 pr-8 text-base text-muted-foreground leading-[1.6]">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>

        {/* CTA block */}
        <Reveal delay={0.1}>
          <div
            data-testid="faq-cta"
            className="mt-16 md:mt-20 border border-border rounded-2xl bg-muted/50 px-8 py-12 md:px-14 md:py-16 flex flex-col lg:flex-row lg:items-center justify-between gap-8"
          >
            <div className="max-w-xl">
              <h3 className="font-display text-3xl md:text-4xl font-medium tracking-tight">Still have questions?</h3>
              <p className="mt-4 text-muted-foreground leading-[1.6]">
                Our team is here to help you find the right printing, packaging, or branding solution for your business.
                Get in touch with us for expert guidance and a customized quotation.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <Link
                to="/contact"
                data-testid="faq-cta-quote"
                className="group inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 py-4 text-sm font-semibold rounded-lg hover:bg-accent transition-colors duration-300"
              >
                Get a Quote
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noreferrer"
                data-testid="faq-cta-talk"
                className="inline-flex items-center justify-center gap-2 border border-border bg-card px-7 py-4 text-sm font-semibold rounded-lg hover:border-accent hover:text-accent transition-colors duration-300"
              >
                <MessageCircle className="h-4 w-4" /> Talk to Our Team
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
